import React, { useEffect } from 'react';

import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

import { ReactComponent as ReloadIcon } from 'assets/images/reload-icon.svg';

import HelptapEditor from '../../helpers';

import nodeControls from '../../node-menu/control-types';

import BaseElement from '../base';

import './index.scss';

function FavouritePageNodeCloner(props) {

  const element = props.element;
  const properties = element.properties;

  const editor = useSlateStatic();

  const elementPath = ReactEditor.findPath(editor, props.element);

  useEffect(function onLoad() {

    return initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function determineWorkingStatus(properties) {

    if (properties.clonerId === null) {
      return `Starting to import ${properties.favouritePageNode.label}...`;
    } else {
      return `Importing ${properties.favouritePageNode.label}...`;
    }

  }

  async function createCloner() {

    HelptapEditor.setNodeBusy(editor, elementPath);

    try {

      const favouritePageNodeId = properties.favouritePageNode._id;

      const cloner = await serverInterface.startup.users.favouritePageNodes.createFavouritePageNodeCloner(favouritePageNodeId, properties.pageId);

      if (cloner.result === void 0) {
        throw new Error('Server returned empty clone');
      }

      HelptapEditor.replaceNode(editor, elementPath, [cloner.result]);

      HelptapEditor.setNodeIdle(editor, elementPath);

    } catch (exception) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: `${properties.favouritePageNode.label} is not available for importing!`,
        seconds: 5,
        type: 'error'
      });
      application.favouritePageNodesManager.tryRemove(properties.favouritePageNode._id);
      HelptapEditor.deleteNodeByPathV2(editor, elementPath);

    }

  }

  async function loadCloner() {

    try {

      const cloner = await serverInterface.startup.users.favouritePageNodes.getFavouritePageNodeClonerById(properties.favouritePageNode._id, properties.clonerId);

      if (cloner.status === 'COMPLETED') {
        HelptapEditor.replaceNode(editor, elementPath, [cloner.result]);
      } else if (cloner.status === 'FAILED') {
        HelptapEditor.setAttributes(editor, {
          properties: {
            ...properties,
            retry: true,
            retryMessage: `Failed to import block ${properties.favouritePageNode.label}. Want to retry?`
          }
        }, elementPath);
      }

    } catch (exception) {

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          retry: true,
          retryMessage: `Importing block ${properties.favouritePageNode.label} expired. Want to retry?`
        }
      }, elementPath);

    }

  }

  function registerEventListeners() {

    function handleCloningCompleted(payload) {

      HelptapEditor.replaceNode(editor, elementPath, [payload.cloner.result]);

    }

    function handleCloningFailed(payload) {

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          retry: true,
          retryMessage: `Failed to import block ${properties.favouritePageNode.label}. Want to retry?`
        }
      }, elementPath);

    }

    application.eventSystem.internal.on(externalEventCodes.FAVOURITE_PAGE_NODE_CLONING_COMPLETED, handleCloningCompleted);
    application.eventSystem.internal.on(externalEventCodes.FAVOURITE_PAGE_NODE_CLONING_FAILED, handleCloningFailed);

    return function cleanup() {

      application.eventSystem.internal.off(externalEventCodes.FAVOURITE_PAGE_NODE_CLONING_COMPLETED, handleCloningCompleted);
      application.eventSystem.internal.off(externalEventCodes.FAVOURITE_PAGE_NODE_CLONING_FAILED, handleCloningFailed);

    };

  }

  function initialize() {

    if (properties.clonerId === null) {
      createCloner();
    } else {
      loadCloner();
    }

    return registerEventListeners();

  }

  function retry() {

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...properties,
        retry: false,
        retryMessage: null,
        clonerId: null
      }
    }, elementPath);

  }

  function tryRenderRetryOptions() {

    if (properties.retry !== true) {
      return;
    }

    return <div className="favourite-page-node-cloner-retry"><label>{properties.retryMessage}</label><button onClick={retry}><ReloadIcon /></button></div>;

  }

  function tryWorkingStatus() {

    if (properties.retry === true) {
      return;
    }

    return <div className="favourite-page-node-cloner-spinner">
    	<label>{determineWorkingStatus(properties)}</label>
    	<ht-spinner size="small" />
    </div>;

  }

  function renderContent() {

    const mainAttributes = {
      ...props.attributes,
      contentEditable: false,
      className: 'favourite-page-node-cloner-content'
    };

    return <div {...mainAttributes}>
      {tryWorkingStatus()}
      {tryRenderRetryOptions()}
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'favourite-page-node-cloner-main',
      disabledNodeControls: true
    };

    return <BaseElement {...baseElementProperties}>
      {renderContent()}
    </BaseElement>;

  }

  return renderMain();

}

export default FavouritePageNodeCloner;
